/**
 * xDataAudit – Frontend API Service Layer
 * Axios instance + typed API functions for all endpoints
 * Uses React Query for caching/invalidation
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

// ─── TYPES ─────────────────────────────────────────────────────────
export interface PaginatedResult<T> {
  data: T[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface ApiError {
  statusCode: number;
  message: string;
  error: string;
  timestamp: string;
  path: string;
}

export interface AuditSession {
  id: string;
  sessionCode: string;
  organizationId: string;
  periodId: string;
  status: 'DRAFT' | 'SUBMITTED' | 'AUDITING' | 'CONCLUDED' | 'ARCHIVED';
  totalScore: number | null;
  ranking: 'A' | 'B' | 'C' | null;
  scoreBreakdown: { pillar_I: number; pillar_II: number; pillar_III: number; pillar_IV: number } | null;
  createdAt: string;
  updatedAt: string;
  organization?: { id: string; name: string; code: string; orgType: string };
  period?: { id: string; name: string; year: number; quarter?: number };
}

export interface ChecklistItem {
  id: string;
  sessionId: string;
  criterion: {
    id: string;
    code: string;
    pillar: string;
    name: string;
    maxScore: number;
    isAutomated: boolean;
    legalReferences: string[];
    complianceReq: string;
  };
  status: 'PASS' | 'PARTIAL' | 'FAIL' | 'NOT_CHECKED';
  score: number;
  maxScore: number;
  auditorNotes: string | null;
  autoCheckedAt: string | null;
  autoResultData: Record<string, unknown> | null;
}

export interface RemediationItem {
  id: string;
  sessionId: string;
  itemCode: string;
  title: string;
  description: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'OVERDUE';
  deadline: string;
  resolvedAt: string | null;
}

export interface Alert {
  id: string;
  organizationId: string;
  alertType: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  title: string;
  description: string;
  isAcknowledged: boolean;
  createdAt: string;
}

export interface NationalDashboard {
  totalOrgs: number;
  totalSessions: number;
  rankingDistribution: { A: number; B: number; C: number };
  avgScore: number;
  openRemediations: number;
  criticalAlerts: number;
  pillarAverages: { pillar_I: number; pillar_II: number; pillar_III: number; pillar_IV: number };
}

export interface CheckJob {
  id: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  progressPct: number;
  checkType: string;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  resultSummary: Record<string, unknown> | null;
}

// ─── AXIOS INSTANCE ────────────────────────────────────────────────
function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  // ── Request interceptor: inject JWT + Correlation ID ────────────
  instance.interceptors.request.use(config => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Request-Id'] = uuidv4();
    return config;
  });

  // ── Response interceptor: unwrap envelope + handle 401 / refresh ─
  instance.interceptors.response.use(
    response => {
      // Backend dùng TransformInterceptor bọc mọi response thành
      // { success: true, data: <actual>, timestamp: "..." }
      // → tự động unwrap để tất cả caller nhận <actual> trực tiếp.
      const d = response.data;
      if (d && typeof d === 'object' && 'success' in d && 'data' in d) {
        response.data = d.data;
      }
      return response;
    },
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retried?: boolean };

      if (error.response?.status === 401 && !originalRequest._retried) {
        originalRequest._retried = true;
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          const res = await axios.post(`${instance.defaults.baseURL}/auth/refresh`, { refreshToken });
          // Refresh endpoint cũng qua TransformInterceptor → unwrap thủ công
          const tokens = res.data?.data ?? res.data;
          localStorage.setItem('access_token', tokens.accessToken);
          localStorage.setItem('refresh_token', tokens.refreshToken);
          instance.defaults.headers.Authorization = `Bearer ${tokens.accessToken}`;
          return instance(originalRequest);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }

      // Re-throw normalized error
      const apiError = error.response?.data as any;
      throw new Error(apiError?.message ?? error.message ?? 'Lỗi kết nối máy chủ');
    },
  );

  return instance;
}

export const apiClient = createApiClient();

// ─── API FUNCTIONS ─────────────────────────────────────────────────

// ─── Types ──────────────────────────────────────────────────────────
export interface UserDto {
  id: string;
  username: string;
  email: string;
  fullName: string;
  roles: string[];
  department: string | null;
  title: string | null;
  mfaEnabled: boolean;
  status: 'ACTIVE' | 'INACTIVE' | 'LOCKED';
  lastLoginAt: string | null;
  createdAt?: string;
  organization?: { id: string; name: string; code: string } | null;
}

export interface CreateUserPayload {
  username: string;
  email: string;
  fullName: string;
  password: string;
  roles: string[];
  department?: string;
  title?: string;
  organizationId?: string;
  mfaEnabled?: boolean;
}

export interface UpdateUserPayload {
  email?: string;
  fullName?: string;
  roles?: string[];
  department?: string;
  title?: string;
  organizationId?: string;
  mfaEnabled?: boolean;
  password?: string;
}

// ── Auth ────────────────────────────────────────────────────────────
export const authApi = {
  login: async (username: string, password: string) => {
    const res = await apiClient.post<{
      accessToken: string;
      refreshToken: string;
      user: UserDto;
    }>('/auth/login', { username, password });
    return res.data;
  },

  refresh: async (refreshToken: string) =>
    apiClient.post<{ accessToken: string; refreshToken: string }>(
      '/auth/refresh', { refreshToken }
    ).then(r => r.data),

  logout: async () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return { success: true };
  },

  me: async () =>
    apiClient.get<UserDto>('/auth/me').then(r => r.data),

  changePassword: async (oldPassword: string, newPassword: string) =>
    apiClient.post('/auth/change-password', { oldPassword, newPassword }).then(r => r.data),
};

// ── Admin Users ─────────────────────────────────────────────────────
export const adminUsersApi = {
  list: (params?: {
    search?: string;
    status?: string;
    role?: string;
    orgId?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResult<UserDto>> =>
    apiClient.get('/admin/users', { params }).then(r => r.data),

  get: (id: string): Promise<UserDto> =>
    apiClient.get(`/admin/users/${id}`).then(r => r.data),

  create: (payload: CreateUserPayload): Promise<UserDto> =>
    apiClient.post('/admin/users', payload).then(r => r.data),

  update: (id: string, payload: UpdateUserPayload): Promise<UserDto> =>
    apiClient.put(`/admin/users/${id}`, payload).then(r => r.data),

  toggleStatus: (id: string): Promise<{ id: string; status: string }> =>
    apiClient.put(`/admin/users/${id}/toggle-status`).then(r => r.data),

  unlock: (id: string): Promise<{ id: string; status: string }> =>
    apiClient.put(`/admin/users/${id}/unlock`).then(r => r.data),

  delete: (id: string): Promise<{ id: string; deleted: boolean }> =>
    apiClient.delete(`/admin/users/${id}`).then(r => r.data),
};

// ── Assessments ─────────────────────────────────────────────────────
export const assessmentApi = {
  list: async (_params: {
    organizationId?: string;
    periodId?: string;
    status?: string;
    ranking?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResult<AuditSession>> => {
    // Development Mock
    return {
      data: [
        {
          id: 'asc-1',
          sessionCode: 'PROTOCOL-2025-001',
          organizationId: 'org-1',
          periodId: 'per-1',
          status: 'SUBMITTED',
          totalScore: 88.5,
          ranking: 'A',
          scoreBreakdown: { pillar_I: 22, pillar_II: 28, pillar_III: 30, pillar_IV: 8.5 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          organization: { id: 'org-1', name: 'Bộ Công an', code: 'BCA', orgType: 'MINISTRY' },
          period: { id: 'per-1', name: 'Kỳ Q1/2025', year: 2025, quarter: 1 }
        },
        {
          id: 'asc-2',
          sessionCode: 'PROTOCOL-2025-002',
          organizationId: 'org-2',
          periodId: 'per-1',
          status: 'AUDITING',
          totalScore: 72.4,
          ranking: 'B',
          scoreBreakdown: { pillar_I: 18, pillar_II: 24, pillar_III: 25, pillar_IV: 5.4 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          organization: { id: 'org-2', name: 'Sở Tài chính TP. Hà Nội', code: 'STC-HN', orgType: 'LOCAL' },
          period: { id: 'per-1', name: 'Kỳ Q1/2025', year: 2025, quarter: 1 }
        },
        {
          id: 'asc-3',
          sessionCode: 'PROTOCOL-2025-003',
          organizationId: 'org-3',
          periodId: 'per-1',
          status: 'CONCLUDED',
          totalScore: 94.2,
          ranking: 'A',
          scoreBreakdown: { pillar_I: 24, pillar_II: 29, pillar_III: 33, pillar_IV: 8.2 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          organization: { id: 'org-3', name: 'Ngân hàng Nhà nước', code: 'SBV', orgType: 'MINISTRY' },
          period: { id: 'per-1', name: 'Kỳ Q1/2025', year: 2025, quarter: 1 }
        }
      ],
      meta: { total: 3, page: 1, limit: 20, totalPages: 1 }
    };
  },

  get: async (id: string): Promise<AuditSession> => {
    return {
      id: id || 'asc-1',
      sessionCode: 'PROTOCOL-2025-001',
      organizationId: 'org-1',
      periodId: 'per-1',
      status: 'SUBMITTED',
      totalScore: 88.5,
      ranking: 'A',
      scoreBreakdown: { pillar_I: 22, pillar_II: 28, pillar_III: 30, pillar_IV: 8.5 },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      organization: { id: 'org-1', name: 'Bộ Công an', code: 'BCA', orgType: 'MINISTRY' },
      period: { id: 'per-1', name: 'Kỳ Q1/2025', year: 2025, quarter: 1 }
    };
  },

  create: (dto: { organizationId: string; periodId: string }) =>
    apiClient.post<AuditSession>('/assessments', dto).then(r => r.data),

  update: (id: string, dto: Partial<AuditSession>) =>
    apiClient.put<AuditSession>(`/assessments/${id}`, dto).then(r => r.data),

  clone: (id: string, sourceSessionId: string, note?: string) =>
    apiClient.post<AuditSession>(`/assessments/${id}/clone`, { sourceSessionId, note }).then(r => r.data),

  submit: (id: string, kpiFormData: Record<string, unknown>) =>
    apiClient.post<AuditSession>(`/assessments/${id}/submit`, { kpiFormData }).then(r => r.data),

  reopen: (id: string) =>
    apiClient.post<AuditSession>(`/assessments/${id}/reopen`).then(r => r.data),

  conclude: (id: string, internalNotes?: string) =>
    apiClient.post<AuditSession>(`/assessments/${id}/conclude`, { internalNotes }).then(r => r.data),

  // Automated checks
  runLegalCheck: (id: string) =>
    apiClient.post<{ message: string; jobId: string }>(`/assessments/${id}/checks/legal`).then(r => r.data),

  runDataQualityCheck: (id: string, config?: object) =>
    apiClient.post<{ message: string; jobId: string }>(`/assessments/${id}/checks/data-quality`, config).then(r => r.data),

  runSecurityCheck: (id: string) =>
    apiClient.post<{ message: string; jobId: string }>(`/assessments/${id}/checks/security`).then(r => r.data),

  getCheckStatus: (sessionId: string, checkId: string) =>
    apiClient.get<CheckJob>(`/assessments/${sessionId}/checks/${checkId}/status`).then(r => r.data),

  getCheckResult: (sessionId: string, checkId: string) =>
    apiClient.get<{ results: object[] }>(`/assessments/${sessionId}/checks/${checkId}/result`).then(r => r.data),

  // Reports
  createReport: (id: string, format: 'PDF' | 'DOCX' | 'XLSX') =>
    apiClient.post<{ reportId: string }>(`/assessments/${id}/reports`, { format }).then(r => r.data),

  downloadReport: (id: string, reportId: string) =>
    apiClient.get(`/assessments/${id}/reports/${reportId}/download`, { responseType: 'blob' }).then(r => r.data),
};

// ── Checklist ───────────────────────────────────────────────────────
export const checklistApi = {
  getBySession: async (sessionId: string): Promise<ChecklistItem[]> => {
    return [
      {
        id: 'itm-1', sessionId, status: 'PASS', score: 10, maxScore: 10, auditorNotes: 'Bằng chứng đầy đủ', autoCheckedAt: null, autoResultData: null,
        criterion: { id: 'crit-1', code: 'TC1a', pillar: 'I_LEGAL', name: 'Tỷ lệ CSDL đã công bố / toàn bộ CSDL (%)', maxScore: 10, isAutomated: true, legalReferences: ['NĐ 278/2025/NĐ-CP, TC1'], complianceReq: 'Phải công bố danh mục toàn bộ CSDL theo quy định' }
      },
      {
        id: 'itm-2', sessionId, status: 'PARTIAL', score: 5, maxScore: 10, auditorNotes: 'Thiếu một số tài liệu hướng dẫn', autoCheckedAt: null, autoResultData: null,
        criterion: { id: 'crit-2', code: 'TC4a', pillar: 'IV_SECURITY', name: 'Tỷ lệ tài sản dữ liệu có biện pháp bảo vệ (%)', maxScore: 10, isAutomated: true, legalReferences: ['NĐ 278/2025/NĐ-CP, TC4'], complianceReq: 'Dữ liệu phải được mã hóa và kiểm soát truy cập' }
      },
      {
        id: 'itm-3', sessionId, status: 'FAIL', score: 0, maxScore: 15, auditorNotes: 'Không có nhật ký truy cập', autoCheckedAt: null, autoResultData: null,
        criterion: { id: 'crit-3', code: 'DL02.1', pillar: 'III_SECURITY', name: 'Kiểm soát truy cập', maxScore: 15, isAutomated: false, legalReferences: ['ISO 27001'], complianceReq: 'Phải ghi nhật ký mọi thao tác dữ liệu' }
      }
    ];
  },

  updateItem: (sessionId: string, itemId: string, dto: {
    status?: string;
    score?: number;
    auditorNotes?: string;
    selfScore?: number;
    selfNotes?: string;
  }) =>
    apiClient.patch<ChecklistItem>(`/assessments/${sessionId}/checklist/${itemId}`, dto).then(r => r.data),
};

// ── Remediations ────────────────────────────────────────────────────
export const remediationApi = {
  getBySession: (sessionId: string) =>
    apiClient.get<RemediationItem[]>(`/assessments/${sessionId}/remediations`).then(r => r.data),

  updateStatus: (id: string, status: string, note?: string) =>
    apiClient.patch<RemediationItem>(`/remediations/${id}/status`, { status, resolutionNote: note }).then(r => r.data),

  uploadEvidence: (id: string, file: File, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return apiClient.post<{ evidenceId: string }>(`/remediations/${id}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
};

// ── Audit Rules ─────────────────────────────────────────────────────
export const rulesApi = {
  list: (organizationId?: string) =>
    apiClient.get('/rules', { params: { organizationId } }).then(r => r.data),

  create: (dto: {
    name: string;
    description?: string;
    ruleType: string;
    ruleConfig: Record<string, unknown>;
    severity?: string;
    organizationId?: string;
  }) => apiClient.post('/rules', dto).then(r => r.data),

  update: (id: string, dto: object) =>
    apiClient.put(`/rules/${id}`, dto).then(r => r.data),

  delete: (id: string) =>
    apiClient.delete(`/rules/${id}`).then(r => r.data),

  run: (id: string, datasourceId: string) =>
    apiClient.post(`/rules/${id}/run`, { datasourceId }).then(r => r.data),
};

// ── Dashboard ───────────────────────────────────────────────────────
export const dashboardApi = {
  national: (year?: number, quarter?: number) =>
    apiClient.get<NationalDashboard>('/dashboard/national', { params: { year, quarter } }).then(r => r.data),

  rankings: (params?: { year?: number; quarter?: number; orgType?: string; province?: string }) =>
    apiClient.get('/dashboard/rankings', { params }).then(r => r.data),
};

// ── Alerts ──────────────────────────────────────────────────────────
export const alertApi = {
  list: (params?: { severity?: string; acknowledged?: boolean; limit?: number }) =>
    apiClient.get<Alert[]>('/alerts', { params }).then(r => r.data),

  acknowledge: (id: string) =>
    apiClient.patch(`/alerts/${id}/acknowledge`).then(r => r.data),
};

// ── Organizations ────────────────────────────────────────────────────
export const orgApi = {
  list: (params?: { orgType?: string; search?: string; page?: number }) =>
    apiClient.get('/admin/organizations', { params }).then(r => r.data),

  get: (id: string) =>
    apiClient.get(`/admin/organizations/${id}`).then(r => r.data),
};

// ─── REACT QUERY KEYS ──────────────────────────────────────────────
export const queryKeys = {
  sessions: {
    all: ['sessions'] as const,
    list: (params: object) => ['sessions', 'list', params] as const,
    detail: (id: string) => ['sessions', 'detail', id] as const,
  },
  checklist: {
    bySession: (sessionId: string) => ['checklist', sessionId] as const,
  },
  remediations: {
    bySession: (sessionId: string) => ['remediations', sessionId] as const,
  },
  dashboard: {
    national: (year?: number, quarter?: number) => ['dashboard', 'national', year, quarter] as const,
    rankings: (params?: object) => ['dashboard', 'rankings', params] as const,
  },
  alerts: {
    all: ['alerts'] as const,
  },
  rules: {
    all: ['rules'] as const,
  },
};

// Default export: raw axios client for agent pages
export default apiClient;
